import React from 'react';
import api from '../api';
import './chatbot.css';

const toLower = (v) => String(v || '').toLowerCase().trim();
const digitsOnly = (p) => String(p || '').replace(/\D/g, '');
const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)];

function stripCountryOrTrunk(raw) {
  let s = String(raw || '').replace(/\D/g, '');
  if (s.startsWith('0091')) s = s.slice(4);
  else if (s.length > 10 && s.startsWith('91')) s = s.slice(2);
  while (s.startsWith('0')) s = s.slice(1);
  return s;
}

function classifyINPhone(raw) {
  const only = digitsOnly(raw);
  if (!only) return { ok: false, nsn: '', e164: '', reason: 'Empty' };
  const nsn = stripCountryOrTrunk(only);
  if (/^[6-9]\d{9}$/.test(nsn)) return { ok: true, nsn, e164: `+91${nsn}` };
  if (/^\d{8,10}$/.test(nsn)) return { ok: true, nsn, e164: `+91${nsn}` };
  return { ok: false, nsn, e164: '', reason: 'Invalid' };
}

function isValidEmail(email) {
  const e = String(email || '').trim();
  if (!e) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(e);
}

function parseSmallTalk(input) {
  const text = toLower(input).replace(/\s+/g, ' ');
  if (/^(hi|hello|hey|hii|hola)\b/.test(text) || /\bgood (morning|afternoon|evening)\b/.test(text)) return 'greeting';
  if (/\bhow are you\b|\bhow r u\b/.test(text)) return 'status';
  if (/\bthanks\b|\bthank you\b|\bthx\b/.test(text)) return 'thanks';
  if (/\b(help|what can you do|commands|options)\b/.test(text)) return 'help';
  if (/^(ok|okay|cool|nice|great|perfect|done)\b/.test(text)) return 'ack';
  if (/\b(bye|goodbye|see you)\b/.test(text)) return 'bye';
  return '';
}

function parseCommand(input) {
  const text = toLower(input).replace(/\s+/g, ' ');
  const customerRegex = '(?:customer|customers|cusotmer|cusotmers)';
  const productRegex = '(?:product|products)';
  const categoryRegex = '(?:category|categories|cat|cats)';
  const userRegex = '(?:user|users)';
  const roleRegex = '(?:role|roles)';
  const licenseRegex = '(?:license|licence)';

  const listCustomer = new RegExp(`^(?:list\\s+${customerRegex}|${customerRegex}\\s+list)(?:\\s+(.+))?$`, 'i');
  const listProduct = new RegExp(`^(?:list\\s+${productRegex}|${productRegex}\\s+list)(?:\\s+(.+))?$`, 'i');
  const listCategory = new RegExp(`^(?:list\\s+${categoryRegex}|${categoryRegex}\\s+list)(?:\\s+(.+))?$`, 'i');
  const listUser = new RegExp(`^(?:list\\s+${userRegex}|${userRegex}\\s+list)(?:\\s+(.+))?$`, 'i');
  const listRole = new RegExp(`^(?:list\\s+${roleRegex}|${roleRegex}\\s+list)(?:\\s+(.+))?$`, 'i');
  const addCustomer = new RegExp(`^add\\s+${customerRegex}$`, 'i');
  const addProduct = new RegExp(`^add\\s+${productRegex}$`, 'i');
  const addCategory = new RegExp(`^add\\s+${categoryRegex}$`, 'i');
  const addUser = new RegExp(`^add\\s+${userRegex}$`, 'i');
  const addRole = new RegExp(`^add\\s+${roleRegex}$`, 'i');
  const updateCustomer = new RegExp(`^update\\s+${customerRegex}(?:\\s+(.+))?$`, 'i');
  const updateProduct = new RegExp(`^update\\s+${productRegex}(?:\\s+(.+))?$`, 'i');
  const updateCategory = new RegExp(`^update\\s+${categoryRegex}(?:\\s+(.+))?$`, 'i');
  const updateUser = new RegExp(`^update\\s+${userRegex}(?:\\s+(.+))?$`, 'i');
  const updateRole = new RegExp(`^update\\s+${roleRegex}(?:\\s+(.+))?$`, 'i');
  const deleteCustomer = new RegExp(`^delete\\s+${customerRegex}(?:\\s+(.+))?$`, 'i');
  const deleteProduct = new RegExp(`^delete\\s+${productRegex}(?:\\s+(.+))?$`, 'i');
  const deleteCategory = new RegExp(`^delete\\s+${categoryRegex}(?:\\s+(.+))?$`, 'i');
  const deleteUser = new RegExp(`^delete\\s+${userRegex}(?:\\s+(.+))?$`, 'i');
  const deleteRole = new RegExp(`^delete\\s+${roleRegex}(?:\\s+(.+))?$`, 'i');
  const viewLicense = new RegExp(`^(?:view\\s+${licenseRegex}|${licenseRegex}\\s+info|${licenseRegex}\\s+status|show\\s+${licenseRegex})$`, 'i');
  const listSales = /^(?:sales\s+list|list\s+sales)(?:\s+(.+))?$/i;
  const viewSaleByInvoice = /^sales\s+([a-z0-9][a-z0-9\-_/]*)$/i;
  const reportHint = /\b(report|summary|dashboard|today|yesterday|weekly|week|monthly|month|monthy|from\s+\d{4}-\d{2}-\d{2})\b/;
  const reportDomain = /\b(sale|sales|return|returns|dashboard)\b/;

  let m = text.match(listCustomer);
  if (m) return { intent: 'list', entity: 'customer', query: (m[1] || '').trim() };
  m = text.match(listProduct);
  if (m) return { intent: 'list', entity: 'product', query: (m[1] || '').trim() };
  m = text.match(listCategory);
  if (m) return { intent: 'list', entity: 'category', query: (m[1] || '').trim() };
  m = text.match(listUser);
  if (m) return { intent: 'list', entity: 'user', query: (m[1] || '').trim() };
  m = text.match(listRole);
  if (m) return { intent: 'list', entity: 'role', query: (m[1] || '').trim() };
  if (addCustomer.test(text)) return { intent: 'add', entity: 'customer' };
  if (addProduct.test(text)) return { intent: 'add', entity: 'product' };
  if (addCategory.test(text)) return { intent: 'add', entity: 'category' };
  if (addUser.test(text)) return { intent: 'add', entity: 'user' };
  if (addRole.test(text)) return { intent: 'add', entity: 'role' };
  m = text.match(updateCustomer);
  if (m) return { intent: 'update', entity: 'customer', query: (m[1] || '').trim() };
  m = text.match(updateProduct);
  if (m) return { intent: 'update', entity: 'product', query: (m[1] || '').trim() };
  m = text.match(updateCategory);
  if (m) return { intent: 'update', entity: 'category', query: (m[1] || '').trim() };
  m = text.match(updateUser);
  if (m) return { intent: 'update', entity: 'user', query: (m[1] || '').trim() };
  m = text.match(updateRole);
  if (m) return { intent: 'update', entity: 'role', query: (m[1] || '').trim() };
  m = text.match(deleteCustomer);
  if (m) return { intent: 'delete', entity: 'customer', query: (m[1] || '').trim() };
  m = text.match(deleteProduct);
  if (m) return { intent: 'delete', entity: 'product', query: (m[1] || '').trim() };
  m = text.match(deleteCategory);
  if (m) return { intent: 'delete', entity: 'category', query: (m[1] || '').trim() };
  m = text.match(deleteUser);
  if (m) return { intent: 'delete', entity: 'user', query: (m[1] || '').trim() };
  m = text.match(deleteRole);
  if (m) return { intent: 'delete', entity: 'role', query: (m[1] || '').trim() };
  if (viewLicense.test(text)) return { intent: 'view', entity: 'license' };
  m = text.match(listSales);
  if (m) return { intent: 'list', entity: 'sale', query: (m[1] || '').trim() };
  m = text.match(viewSaleByInvoice);
  if (m) {
    const q = (m[1] || '').trim();
    const reserved = new Set(['report', 'summary', 'today', 'yesterday', 'week', 'weekly', 'month', 'monthly', 'monthy', 'dashboard']);
    if (!reserved.has(q)) return { intent: 'view', entity: 'sale', query: q };
  }
  if (reportHint.test(text) && reportDomain.test(text)) return { intent: 'report', raw: text };
  const smallTalkKind = parseSmallTalk(text);
  if (smallTalkKind) return { intent: 'smalltalk', kind: smallTalkKind };

  return { intent: 'unknown' };
}

function Message({ role, text }) {
  return (
    <div className={`chatbot-msg ${role}`}>
      <div className={`chatbot-avatar ${role}`}>
        {role === 'bot' ? (
          <span className="chatbot-avatar-bot">
            <span className="chatbot-avatar-antenna" />
            <span className="chatbot-avatar-eye left" />
            <span className="chatbot-avatar-eye right" />
            <span className="chatbot-avatar-mouth" />
            <span className="chatbot-avatar-hand" />
          </span>
        ) : (
          <span className="chatbot-avatar-human">
            <span className="chatbot-avatar-hair" />
            <span className="chatbot-avatar-head" />
            <span className="chatbot-avatar-glass left" />
            <span className="chatbot-avatar-glass right" />
            <span className="chatbot-avatar-eye left" />
            <span className="chatbot-avatar-eye right" />
            <span className="chatbot-avatar-mouth" />
            <span className="chatbot-avatar-body" />
            <span className="chatbot-avatar-hand" />
          </span>
        )}
      </div>
      <div className="chatbot-bubble-wrap">
        <div className="chatbot-bubble">{text}</div>
      </div>
    </div>
  );
}

function ymd(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseReportRange(raw) {
  const text = toLower(raw);
  const now = new Date();
  const today = ymd(now);

  const rangeRe = /(?:from\s+)?(\d{4}-\d{2}-\d{2})\s*(?:to|-)\s*(\d{4}-\d{2}-\d{2})/i;
  const m = text.match(rangeRe);
  if (m) return { from: m[1], to: m[2], label: `${m[1]} to ${m[2]}` };

  if (text.includes('today')) return { from: today, to: today, label: 'Today' };
  if (text.includes('yesterday')) {
    const d = new Date(now);
    d.setDate(d.getDate() - 1);
    const p = ymd(d);
    return { from: p, to: p, label: 'Yesterday' };
  }
  if (text.includes('weekly') || text.includes('week')) {
    const fromD = new Date(now);
    fromD.setDate(fromD.getDate() - 6);
    return { from: ymd(fromD), to: today, label: 'Last 7 days' };
  }
  if (text.includes('monthly') || text.includes('monthy') || text.includes('month')) {
    const fromD = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(fromD), to: today, label: 'This month' };
  }
  const fromD = new Date(now);
  fromD.setDate(fromD.getDate() - 29);
  return { from: ymd(fromD), to: today, label: 'Last 30 days' };
}

function parseReportType(raw) {
  const text = toLower(raw);
  if (text.includes('dashboard')) return 'dashboard';
  if (text.includes('return')) return 'returns';
  return 'sales';
}

export default function ChatBotWidget() {
  const SUGGESTION_PREVIEW_COUNT = 8;
  const quickSuggestions = React.useMemo(() => ([
    'list sales',
    'sales report today',
    'sales report weekly',
    'sales report monthly',
    'return report today',
    'return report weekly',
    'return report monthly',
    'dashboard report weekly',
    'dashboard report monthly',
    'list customer',
    'add customer',
    'update customer',
    'delete customer',
    'list product',
    'add product',
    'update product',
    'delete product',
    'list category',
    'add category',
    'list user',
    'add user',
    'update user',
    'list role',
    'add role',
    'update role',
    'delete role',
    'license info'
  ]), []);
  const [theme, setTheme] = React.useState('blue');
  const [open, setOpen] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const [dragMoved, setDragMoved] = React.useState(false);
  const widgetSize = React.useCallback((isOpen) => (isOpen ? { w: 380, h: 460 } : { w: 78, h: 48 }), []);
  const defaultPos = React.useCallback((isOpen) => {
    const { h } = widgetSize(isOpen);
    return {
      x: 16,
      y: Math.max(window.innerHeight - h - 16, 8),
    };
  }, [widgetSize]);
  const [position, setPosition] = React.useState(() => defaultPos(false));
  const dragRef = React.useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(true);
  const [showAllSuggestions, setShowAllSuggestions] = React.useState(false);
  const [messages, setMessages] = React.useState([
    { role: 'bot', text: 'Choose a quick suggestion below or type your command.' }
  ]);
  const [results, setResults] = React.useState({ entity: '', rows: [], expanded: {}, action: 'list' });
  const [selected, setSelected] = React.useState({ customer: null, product: null, category: null, user: null, role: null });
  const [draft, setDraft] = React.useState(null);
  const [draftData, setDraftData] = React.useState(null);
  const [saveStatus, setSaveStatus] = React.useState(null);
  const [customerStateGuard, setCustomerStateGuard] = React.useState({ locked: false, initial: '' });
  const [categoryOptions, setCategoryOptions] = React.useState([]);
  const [roleOptions, setRoleOptions] = React.useState([]);
  const scrollRef = React.useRef(null);
  const prevMsgLenRef = React.useRef(messages.length);
  const inr = React.useMemo(() => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }), []);

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('sellantra.chat.theme');
      if (saved === 'blue' || saved === 'orange' || saved === 'green') setTheme(saved);
    } catch {}
  }, []);

  function applyTheme(next) {
    setTheme(next);
    try { localStorage.setItem('sellantra.chat.theme', next); } catch {}
  }

  function cycleTheme() {
    const order = ['blue', 'orange', 'green'];
    const idx = order.indexOf(theme);
    const next = order[(idx + 1) % order.length];
    applyTheme(next);
  }

  React.useEffect(() => {
    const prev = prevMsgLenRef.current;
    if (messages.length > prev) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevMsgLenRef.current = messages.length;
  }, [messages.length]);

  React.useEffect(() => {
    if (!showSuggestions || draft) setShowAllSuggestions(false);
  }, [showSuggestions, draft]);

  function pushMessage(role, text) {
    setMessages((prev) => [...prev, { role, text }]);
  }

  function markerValue(entity, row) {
    if (!row) return '-';
    if (entity === 'customer') return row.phone || '-';
    if (entity === 'product') return row.sku || '-';
    if (entity === 'user') return row.email || '-';
    if (entity === 'license') return row.status || '-';
    if (entity === 'report') return row.kind || '-';
    if (entity === 'sale') return row.invoice_no || '-';
    return row.name || '-';
  }

  function markerLabel(entity) {
    if (entity === 'customer') return 'Phone';
    if (entity === 'product') return 'SKU';
    if (entity === 'user') return 'Email';
    if (entity === 'license') return 'Status';
    if (entity === 'report') return 'Type';
    if (entity === 'sale') return 'Invoice';
    return 'Name';
  }

  function clampPosFor(x, y, isOpen) {
    const { w, h } = widgetSize(isOpen);
    return {
      x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - w - 8)),
      y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - h - 8)),
    };
  }

  function toggleOpen(nextOpen) {
    if (nextOpen === open) return;
    const from = widgetSize(open);
    const to = widgetSize(nextOpen);
    setPosition((p) => {
      const adjustedY = p.y - (to.h - from.h);
      return clampPosFor(p.x, adjustedY, nextOpen);
    });
    setOpen(nextOpen);
  }

  function onDragStart(e) {
    setDragging(true);
    setDragMoved(false);
    dragRef.current = {
      x: position.x,
      y: position.y,
      startX: e.clientX,
      startY: e.clientY,
    };
    e.preventDefault();
  }

  React.useEffect(() => {
    if (!dragging) return;
    function onMove(e) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setDragMoved(true);
      setPosition(clampPosFor(dragRef.current.x + dx, dragRef.current.y + dy, open));
    }
    function onUp() { setDragging(false); }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, open]);

  React.useEffect(() => {
    function onResize() {
      setPosition((p) => clampPosFor(p.x, p.y, open));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  async function hasAnySales(customerId) {
    try {
      const r = await api.get('/sales', { params: { pageSize: 500, sort: 'date_desc' } });
      const rows = Array.isArray(r.data) ? r.data : [];
      return rows.some((s) => Number(s.customer_id || 0) === Number(customerId));
    } catch {
      return true; // conservative
    }
  }

  function openDraft(entity, mode, payload, id = null) {
    const p = payload || {};
    const mapped = entity === 'customer'
      ? {
          id: id ?? p.id ?? '',
          name: p.name || '',
          phone: p.phone || '',
          email: p.email || '',
          address: p.address || '',
          state_code: p.state_code || '',
          gstin: p.gstin || '',
          tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
          store_credit: p.store_credit ?? 0,
        }
      : entity === 'product'
      ? {
          id: id ?? p.id ?? '',
          name: p.name || '',
          sku: p.sku || '',
          barcode: p.barcode || '',
          price: p.price ?? 0,
          gst_rate: p.gst_rate ?? 0,
          discount_pct: p.discount_pct ?? 0,
          stock: p.stock ?? 0,
          low_stock_threshold: p.low_stock_threshold ?? 0,
          category_id: p.category_id == null ? '' : String(p.category_id),
        }
      : entity === 'category'
      ? {
          id: id ?? p.id ?? '',
          name: p.name || '',
        }
      : entity === 'role'
      ? {
          id: id ?? p.id ?? '',
          name: p.name || '',
          description: p.description || '',
          permissions: Array.isArray(p.permissions) ? p.permissions.join(', ') : (p.permissions || ''),
          built_in: !!p.built_in,
          users_count: Number(p.users_count || 0),
        }
      : {
          id: id ?? p.id ?? '',
          name: p.name || '',
          email: p.email || '',
          password: '',
          is_active: typeof p.is_active === 'boolean' ? p.is_active : true,
          role_name: Array.isArray(p.roles) && p.roles.length ? String(p.roles[0]) : 'Viewer',
        };
    setDraft({ entity, mode, id });
    setDraftData(mapped);
    setSaveStatus(null);
    if (entity !== 'customer' || mode !== 'update') {
      setCustomerStateGuard({ locked: false, initial: '' });
    } else {
      const initial = String(mapped.state_code || '').trim().toUpperCase();
      setCustomerStateGuard({ locked: false, initial });
      hasAnySales(id || payload?.id).then((locked) => {
        setCustomerStateGuard({ locked: !!locked, initial });
      });
    }
    if (entity === 'product') {
      fetchCategories('', 0).then((rows) => {
        setCategoryOptions(rows);
        const uncategorized = rows.find((c) => toLower(c.name) === 'uncategorized');
        const fallbackId = uncategorized?.id ?? rows[0]?.id ?? '';
        setDraftData((d) => {
          if (!d || !Object.prototype.hasOwnProperty.call(d, 'sku')) return d;
          if (Number(d.id || 0) !== Number(mapped.id || 0)) return d;
          if (String(d.category_id || '').trim()) return d;
          return { ...d, category_id: fallbackId === '' ? '' : String(fallbackId) };
        });
      }).catch(() => {});
    }
    if (entity === 'user') {
      fetchRoles('', 0).then((rows) => {
        setRoleOptions(rows);
        const hasViewer = rows.some((r) => toLower(r.name) === 'viewer');
        const fallback = hasViewer ? 'Viewer' : (rows[0]?.name || '');
        setDraftData((d) => {
          if (!d || !Object.prototype.hasOwnProperty.call(d, 'is_active')) return d;
          if (Number(d.id || 0) !== Number(mapped.id || 0)) return d;
          if (String(d.role_name || '').trim()) return d;
          return { ...d, role_name: fallback };
        });
      }).catch(() => {});
    }
  }

  async function fetchCustomers(query = '', limit = 15) {
    const res = await api.get('/customers' + (query ? `?search=${encodeURIComponent(query)}` : ''));
    const rows = Array.isArray(res.data) ? res.data : [];
    return limit > 0 ? rows.slice(0, limit) : rows;
  }

  async function fetchProducts(query = '', limit = 15) {
    if (query) {
      const s = await api.get('/products/search', { params: { q: query, limit: Math.max(limit || 15, 15) } });
      const rows = Array.isArray(s.data) ? s.data : [];
      return limit > 0 ? rows.slice(0, limit) : rows;
    }
    const r = await api.get('/products');
    const rows = Array.isArray(r.data) ? r.data : [];
    return limit > 0 ? rows.slice(0, limit) : rows;
  }

  async function fetchCategories(query = '', limit = 15) {
    const res = await api.get('/product-categories');
    const rows = Array.isArray(res.data) ? res.data : [];
    const q = toLower(query);
    const filtered = q ? rows.filter((c) => toLower(c.name).includes(q)) : rows;
    return limit > 0 ? filtered.slice(0, limit) : filtered;
  }

  async function fetchUsers(query = '', limit = 15) {
    const params = query ? { q: query } : undefined;
    const res = await api.get('/users', { params });
    const rows = Array.isArray(res.data) ? res.data : [];
    return limit > 0 ? rows.slice(0, limit) : rows;
  }

  async function fetchRoles(query = '', limit = 15) {
    const res = await api.get('/roles');
    const rows = Array.isArray(res.data) ? res.data : [];
    const q = toLower(query);
    const filtered = q
      ? rows.filter((r) => toLower(r.name).includes(q) || toLower(r.description).includes(q))
      : rows;
    return limit > 0 ? filtered.slice(0, limit) : filtered;
  }

  async function fetchLicenseInfo() {
    const res = await api.get('/license/status');
    return res?.data || null;
  }

  async function fetchSalesReport(range) {
    const params = { from: range.from, to: range.to };
    const [summaryRes, salesRes] = await Promise.all([
      api.get('/reports/sales', { params }),
      api.get('/sales', { params: { ...params, limit: 'all', sort: 'date_desc' } }),
    ]);
    const summary = summaryRes?.data || {};
    const sales = Array.isArray(salesRes?.data) ? salesRes.data : [];

    let cash = 0; let upi = 0; let card = 0;
    for (const s of sales) {
      const amt = Number(s.total || 0);
      const pm = String(s.payment_method || '').toLowerCase();
      if (pm.includes('cash')) cash += amt;
      else if (pm.includes('upi')) upi += amt;
      else card += amt;
    }

    return {
      id: `sales-${range.from}-${range.to}`,
      kind: 'sales',
      name: `Sales Report (${range.label})`,
      status: 'ready',
      from: range.from,
      to: range.to,
      bills: Number(summary.bills || sales.length || 0),
      total: Number(summary.total || 0),
      cgst: Number(summary.cgst || 0),
      sgst: Number(summary.sgst || 0),
      igst: Number(summary.igst || 0),
      payment_cash: cash,
      payment_upi: upi,
      payment_card: card,
      avg_bill: Number(summary.bills || 0) > 0 ? Number(summary.total || 0) / Number(summary.bills || 1) : 0,
    };
  }

  async function fetchReturnsReport(range) {
    const params = { from: range.from, to: range.to };
    const [summaryRes, listRes] = await Promise.all([
      api.get('/returns/summary', { params }),
      api.get('/returns', { params: { ...params, limit: 'all', sort: 'date_desc' } }),
    ]);
    const summary = summaryRes?.data || {};
    const rows = Array.isArray(listRes?.data) ? listRes.data : [];
    return {
      id: `returns-${range.from}-${range.to}`,
      kind: 'returns',
      name: `Returns Report (${range.label})`,
      status: 'ready',
      from: range.from,
      to: range.to,
      returns_count: rows.length,
      total_returns: Number(summary.total_returns || 0),
      top_return_products: Array.isArray(summary.top_return_products) ? summary.top_return_products.slice(0, 3) : [],
      top_return_customers: Array.isArray(summary.top_return_customers) ? summary.top_return_customers.slice(0, 3) : [],
    };
  }

  async function fetchDashboardReport(range) {
    const [sales, returns] = await Promise.all([fetchSalesReport(range), fetchReturnsReport(range)]);
    return {
      id: `dashboard-${range.from}-${range.to}`,
      kind: 'dashboard',
      name: `Dashboard Snapshot (${range.label})`,
      status: 'ready',
      from: range.from,
      to: range.to,
      sales_total: Number(sales.total || 0),
      sales_bills: Number(sales.bills || 0),
      returns_total: Number(returns.total_returns || 0),
      returns_count: Number(returns.returns_count || 0),
      net_after_returns: Number(sales.total || 0) - Number(returns.total_returns || 0),
    };
  }

  async function fetchSalesList(query = '', limit = 15) {
    const params = query ? { search: query, sort: 'date_desc', limit: 'all' } : { sort: 'date_desc', limit: 200 };
    const res = await api.get('/sales', { params });
    const rows = Array.isArray(res.data) ? res.data : [];
    return limit > 0 ? rows.slice(0, limit) : rows;
  }

  async function fetchSaleDetailById(id) {
    const res = await api.get(`/sales/${id}`);
    return res?.data || null;
  }

  async function fetchSaleByInvoice(invoiceNo) {
    const rows = await fetchSalesList(invoiceNo, 0);
    const q = String(invoiceNo || '').trim().toLowerCase();
    const exact = rows.find((r) => String(r.invoice_no || '').trim().toLowerCase() === q);
    const picked = exact || (rows.length === 1 ? rows[0] : null);
    if (!picked) return { row: null, many: rows.slice(0, 15) };
    const detail = await fetchSaleDetailById(picked.id);
    return { row: detail || picked, many: rows.slice(0, 15) };
  }

  async function fetchValidPermissionNames() {
    try {
      const r = await api.get('/roles/all/perms');
      if (Array.isArray(r.data)) return r.data.map((x) => String(x || '').trim()).filter(Boolean);
    } catch {}
    try {
      const r2 = await api.get('/roles/permissions');
      if (Array.isArray(r2.data)) return r2.data.map((x) => String(x?.name || '').trim()).filter(Boolean);
    } catch {}
    return [];
  }

  async function findOneByQuery(entity, query) {
    const clean = String(query || '').trim();
    const rows = entity === 'customer'
      ? await fetchCustomers(clean, 15)
      : entity === 'product'
      ? await fetchProducts(clean, 15)
      : entity === 'category'
      ? await fetchCategories(clean, 15)
      : entity === 'user'
      ? await fetchUsers(clean, 15)
      : await fetchRoles(clean, 15);
    if (!rows.length) return { found: null, rows };
    if (clean && /^\d+$/.test(clean)) {
      const fullRows = entity === 'customer'
        ? await fetchCustomers('', 0)
        : entity === 'product'
        ? await fetchProducts('', 0)
        : entity === 'category'
        ? await fetchCategories('', 0)
        : entity === 'user'
        ? await fetchUsers('', 0)
        : await fetchRoles('', 0);
      const byId = fullRows.find((r) => Number(r.id) === Number(clean));
      if (byId) return { found: byId, rows };
    }
    if (rows.length === 1) return { found: rows[0], rows };
    return { found: null, rows };
  }

  async function beginAction(parsed) {
    const { intent, entity, query = '' } = parsed;
    if (intent === 'add') {
      if (entity === 'customer') {
        openDraft('customer', 'add', {
          name: '',
          phone: '',
          email: '',
          address: '',
          state_code: '',
          gstin: '',
          tags: [],
          store_credit: 0
        });
        pushMessage('bot', 'Customer add form opened. Fill fields and click Save.');
      } else {
        if (entity === 'user') {
          openDraft('user', 'add', {
            name: '',
            email: '',
            password: '',
            is_active: true,
            role_name: 'Viewer',
          });
          pushMessage('bot', 'User add form opened. Fill fields and click Save.');
          return;
        }
        if (entity === 'role') {
          openDraft('role', 'add', { name: '', description: '', permissions: '' });
          pushMessage('bot', 'Role add form opened. Fill fields and click Save.');
          return;
        }
        if (entity === 'category') {
          openDraft('category', 'add', { name: '' });
          pushMessage('bot', 'Category add form opened. Fill name and click Save.');
          return;
        }
        openDraft('product', 'add', {
          name: '',
          sku: '',
          barcode: '',
          price: 0,
          gst_rate: 0,
          discount_pct: 0,
          stock: 0,
          low_stock_threshold: 0,
          category_id: ''
        });
        pushMessage('bot', 'Product add form opened. Fill fields and click Save.');
      }
      return;
    }

    const alreadySelected = selected[entity];
    if (!query && alreadySelected) {
      setResults({ entity, rows: [alreadySelected], expanded: { [alreadySelected.id]: true }, action: intent });
      openDraft(entity, intent, alreadySelected, alreadySelected.id);
      pushMessage('bot', `${entity} ${intent} draft opened from selected ${entity}.`);
      return;
    }

    const match = await findOneByQuery(entity, query);
    if (match.found) {
      setResults({ entity, rows: [match.found], expanded: { [match.found.id]: true }, action: intent });
      setSelected((s) => ({ ...s, [entity]: match.found }));
      openDraft(entity, intent, match.found, match.found.id);
      pushMessage('bot', `${entity} ${intent} form opened for "${match.found.name || ''}".`);
      return;
    }

    setResults({
      entity,
      rows: match.rows,
      expanded: {},
      action: intent,
    });
    if (!match.rows.length) pushMessage('bot', `No ${entity} matched "${query}".`);
    else pushMessage('bot', `Multiple ${entity}s matched. Expand a card and click ${intent === 'delete' ? 'Delete' : 'Edit'}.`);
  }

  async function validateAndPush() {
    if (!draft || !draftData) return;
    setSaveStatus(null);
    const report = (type, text) => setSaveStatus({ type, text });

    const entity = draft.entity;
    const mode = draft.mode;
    const id = draft.id || draftData.id;
    let payload = {};

    try {
    if (entity === 'customer') {
      const tagsArr = String(draftData.tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      payload = {
        name: String(draftData.name || '').trim(),
        phone: String(draftData.phone || '').trim(),
        email: String(draftData.email || '').trim(),
        address: String(draftData.address || '').trim(),
        state_code: String(draftData.state_code || '').trim().toUpperCase(),
        gstin: String(draftData.gstin || '').trim().toUpperCase(),
        tags: tagsArr,
        store_credit: Math.max(0, Number(draftData.store_credit || 0)),
      };

      const allRows = await fetchCustomers('', 0);
      if (mode !== 'delete') {
        if (!String(payload.name || '').trim()) {
          const msg = 'Customer name is required.';
          report('error', msg);
          return;
        }
        if (!isValidEmail(payload.email)) {
          const msg = 'Save failed: enter a valid email address or leave it blank.';
          report('error', msg);
          return;
        }
        if (payload.state_code && !/^[A-Z]{2}$/.test(payload.state_code)) {
          const msg = 'Save failed: state code must be a valid 2-letter code (e.g., OD, KA).';
          report('error', msg);
          return;
        }
        const phoneInfo = classifyINPhone(payload.phone);
        if (!phoneInfo.ok) {
          const msg = 'Customer phone is invalid.';
          report('error', msg);
          return;
        }
        payload.phone = phoneInfo.e164;
        const dup = allRows.some((c) => stripCountryOrTrunk(c.phone || '') === phoneInfo.nsn && Number(c.id) !== Number(id || 0));
        if (dup) {
          const msg = 'Save failed: duplicate phone number.';
          report('error', msg);
          return;
        }
        if (mode === 'update' && customerStateGuard.locked) {
          const next = String(payload.state_code || '').trim().toUpperCase();
          const prev = String(customerStateGuard.initial || '').trim().toUpperCase();
          if (next !== prev) {
            const msg = 'Save failed: state code is locked after first sale.';
            report('error', msg);
            return;
          }
        }
      }

      if (mode === 'add') {
        const { data } = await api.post('/customers', payload);
        setSelected((s) => ({ ...s, customer: data }));
        const msg = `Saved successfully. Customer ${data?.name || ''} created.`;
        report('ok', msg);
      } else if (mode === 'update') {
        if (!id) {
          const msg = 'Customer id is required for update.';
          report('error', msg);
          return;
        }
        const { data } = await api.put(`/customers/${id}`, payload);
        setSelected((s) => ({ ...s, customer: data }));
        const msg = `Saved successfully. Customer ${data?.name || payload.name || ''} updated.`;
        report('ok', msg);
      } else if (mode === 'delete') {
        if (!id) {
          const msg = 'Customer id is required for delete.';
          report('error', msg);
          return;
        }
        await api.delete(`/customers/${id}`);
        setSelected((s) => ({ ...s, customer: null }));
        const msg = `Saved successfully. Customer ${payload.name || ''} deleted.`;
        report('ok', msg);
      }
    } else if (entity === 'product') {
      const allCats = await fetchCategories('', 0);
      const uncategorized = allCats.find((c) => toLower(c.name) === 'uncategorized');
      const fallbackCatId = uncategorized?.id ?? allCats[0]?.id ?? null;
      const chosenCatIdRaw = String(draftData.category_id || '').trim();
      const chosenCatId = chosenCatIdRaw ? Number(chosenCatIdRaw) : Number(fallbackCatId || 0);
      payload = {
        name: String(draftData.name || '').trim(),
        sku: String(draftData.sku || '').trim(),
        barcode: String(draftData.barcode || '').trim(),
        price: Number(draftData.price || 0),
        gst_rate: Number(draftData.gst_rate || 0),
        discount_pct: Number(draftData.discount_pct || 0),
        stock: Number(draftData.stock || 0),
        low_stock_threshold: Number(draftData.low_stock_threshold || 0),
        category_id: Number.isFinite(chosenCatId) && chosenCatId > 0 ? chosenCatId : null,
      };

      const allRows = await fetchProducts('', 0);
      if (mode !== 'delete') {
        if (!String(payload.name || '').trim() || !String(payload.sku || '').trim()) {
          const msg = 'Product name and sku are required.';
          report('error', msg);
          return;
        }
        if (!allCats.length) {
          report('error', 'Save failed: category list is empty.');
          return;
        }
        const categoryExists = allCats.some((c) => Number(c.id) === Number(payload.category_id || 0));
        if (!categoryExists) {
          report('error', 'Save failed: category does not exist.');
          return;
        }
        const skuKey = toLower(payload.sku);
        const dup = allRows.some((p) => toLower(p.sku) === skuKey && Number(p.id) !== Number(id || 0));
        if (dup) {
          const msg = 'Save failed: duplicate SKU.';
          report('error', msg);
          return;
        }
      }

      if (mode === 'add') {
        const { data } = await api.post('/products', payload);
        setSelected((s) => ({ ...s, product: data }));
        const msg = `Saved successfully. Product ${data?.name || ''} created.`;
        report('ok', msg);
      } else if (mode === 'update') {
        if (!id) {
          const msg = 'Product id is required for update.';
          report('error', msg);
          return;
        }
        const { data } = await api.put(`/products/${id}`, payload);
        setSelected((s) => ({ ...s, product: data }));
        const msg = `Saved successfully. Product ${data?.name || payload.name || ''} updated.`;
        report('ok', msg);
      } else if (mode === 'delete') {
        if (!id) {
          const msg = 'Product id is required for delete.';
          report('error', msg);
          return;
        }
        await api.delete(`/products/${id}`);
        setSelected((s) => ({ ...s, product: null }));
        const msg = `Saved successfully. Product ${payload.name || ''} deleted.`;
        report('ok', msg);
      }
    } else if (entity === 'category') {
      payload = { name: String(draftData.name || '').trim() };
      const allRows = await fetchCategories('', 0);
      if (mode !== 'delete') {
        if (!payload.name) {
          report('error', 'Category name is required.');
          return;
        }
        const key = toLower(payload.name);
        const dup = allRows.some((c) => toLower(c.name) === key && Number(c.id) !== Number(id || 0));
        if (dup) {
          report('error', 'Save failed: duplicate category name.');
          return;
        }
      }

      if (mode === 'add') {
        const { data } = await api.post('/product-categories', payload);
        setSelected((s) => ({ ...s, category: data }));
        report('ok', `Saved successfully. Category ${data?.name || payload.name} created.`);
      } else if (mode === 'update') {
        if (!id) {
          report('error', 'Category id is required for update.');
          return;
        }
        const { data } = await api.put(`/product-categories/${id}`, payload);
        setSelected((s) => ({ ...s, category: data }));
        report('ok', `Saved successfully. Category ${data?.name || payload.name} updated.`);
      } else if (mode === 'delete') {
        if (!id) {
          report('error', 'Category id is required for delete.');
          return;
        }
        await api.delete(`/product-categories/${id}`);
        setSelected((s) => ({ ...s, category: null }));
        report('ok', `Saved successfully. Category ${payload.name || ''} deleted.`);
      }
    } else if (entity === 'user') {
      const allRoles = await fetchRoles('', 0);
      payload = {
        name: String(draftData.name || '').trim(),
        email: String(draftData.email || '').trim().toLowerCase(),
        password: String(draftData.password || '').trim(),
        is_active: !!draftData.is_active,
        role_name: String(draftData.role_name || '').trim(),
      };

      const allRows = await fetchUsers('', 0);
      if (mode !== 'delete') {
        if (!payload.name || !payload.email) {
          report('error', 'User name and email are required.');
          return;
        }
        if (!payload.role_name) {
          report('error', 'Role is required.');
          return;
        }
        const roleExists = allRoles.some((r) => toLower(r.name) === toLower(payload.role_name));
        if (!roleExists) {
          report('error', 'Save failed: selected role does not exist.');
          return;
        }
        const dup = allRows.some((u) => toLower(u.email) === toLower(payload.email) && Number(u.id) !== Number(id || 0));
        if (dup) {
          report('error', 'Save failed: duplicate email id.');
          return;
        }
      }

      if (mode === 'add') {
        if (!payload.password || payload.password.length < 8) {
          report('error', 'Password must be at least 8 characters.');
          return;
        }
        const { data } = await api.post('/users', {
          name: payload.name,
          email: payload.email,
          password: payload.password,
          roles: [payload.role_name],
        });
        setSelected((s) => ({ ...s, user: data }));
        report('ok', `Saved successfully. User ${data?.name || payload.name} created.`);
      } else if (mode === 'update') {
        if (!id) {
          report('error', 'User id is required for update.');
          return;
        }
        // Backend does not support email update on PATCH /users/:id.
        const current = allRows.find((u) => Number(u.id) === Number(id));
        if (current && toLower(current.email) !== toLower(payload.email)) {
          report('error', 'Save failed: email update is not supported for user.');
          return;
        }
        const patchBody = { name: payload.name, is_active: payload.is_active };
        const { data } = await api.patch(`/users/${id}`, patchBody);
        await api.patch(`/users/${id}/roles`, { roles: [payload.role_name] });
        const merged = { ...data, roles: [payload.role_name] };
        setSelected((s) => ({ ...s, user: merged }));
        report('ok', `Saved successfully. User ${data?.name || payload.name} updated.`);
      } else if (mode === 'delete') {
        if (!id) {
          report('error', 'User id is required for delete.');
          return;
        }
        await api.post(`/users/${id}/activate`, { active: false });
        setSelected((s) => ({ ...s, user: null }));
        report('ok', `Saved successfully. User ${payload.name || ''} deactivated.`);
      }
    } else {
      const permList = String(draftData.permissions || '')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      payload = {
        name: String(draftData.name || '').trim(),
        description: String(draftData.description || '').trim(),
        permissions: permList,
      };

      const allRows = await fetchRoles('', 0);
      const current = allRows.find((r) => Number(r.id) === Number(id));
      const validPerms = await fetchValidPermissionNames();
      const validSet = new Set(validPerms);
      if (mode !== 'delete') {
        if (!payload.name) {
          report('error', 'Role name is required.');
          return;
        }
        const dup = allRows.some((r) => toLower(r.name) === toLower(payload.name) && Number(r.id) !== Number(id || 0));
        if (dup) {
          report('error', 'Save failed: duplicate role name.');
          return;
        }
        if (validPerms.length) {
          const invalid = permList.filter((p) => !validSet.has(p));
          if (invalid.length) {
            const suggMap = new Map(validPerms.map((p) => [toLower(p), p]));
            const hints = invalid
              .map((bad) => {
                const sug = suggMap.get(toLower(bad));
                return sug && sug !== bad ? `${bad} (did you mean ${sug}?)` : bad;
              })
              .join(', ');
            report('error', `Save failed: permission does not exist: ${hints}`);
            return;
          }
        }
        if (mode === 'update' && current?.built_in) {
          const prevName = String(current.name || '');
          const prevDesc = String(current.description || '');
          if (payload.name !== prevName || payload.description !== prevDesc) {
            report('error', 'Save failed: built-in role name/description cannot be changed.');
            return;
          }
        }
      }

      if (mode === 'add') {
        const { data } = await api.post('/roles', {
          name: payload.name,
          description: payload.description || null,
          permissions: payload.permissions,
        });
        setSelected((s) => ({ ...s, role: data }));
        report('ok', `Saved successfully. Role ${data?.name || payload.name} created.`);
      } else if (mode === 'update') {
        if (!id) {
          report('error', 'Role id is required for update.');
          return;
        }
        const { data } = await api.put(`/roles/${id}`, {
          name: payload.name,
          description: payload.description,
          permissions: payload.permissions,
        });
        setSelected((s) => ({ ...s, role: data }));
        report('ok', `Saved successfully. Role ${data?.name || payload.name} updated.`);
      } else if (mode === 'delete') {
        if (!id) {
          report('error', 'Role id is required for delete.');
          return;
        }
        if (current?.built_in) {
          report('error', 'Save failed: cannot delete a built-in role.');
          return;
        }
        if (Number(current?.users_count || 0) > 0) {
          report('error', 'Save failed: role is assigned to users.');
          return;
        }
        await api.delete(`/roles/${id}`);
        setSelected((s) => ({ ...s, role: null }));
        report('ok', `Saved successfully. Role ${payload.name || ''} deleted.`);
      }
    }
    } catch (err) {
      const raw = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Save failed';
      const low = String(raw).toLowerCase();
      let msg = `Save failed: ${raw}`;
      if (low.includes('duplicate') && low.includes('phone')) msg = 'Save failed: duplicate phone number.';
      else if (low.includes('customers_phone_key')) msg = 'Save failed: duplicate phone number.';
      else if (low.includes('sku') && low.includes('exists')) msg = 'Save failed: duplicate SKU.';
      else if (low.includes('already exists') && low.includes('category')) msg = 'Save failed: duplicate category name.';
      else if (low.includes('email') && low.includes('exist')) msg = 'Save failed: duplicate email id.';
      else if (low.includes('role name') && low.includes('exists')) msg = 'Save failed: duplicate role name.';
      report('error', msg);
    }
  }

  async function executeCommand(text) {
    if (!text || busy) return;

    setBusy(true);
    pushMessage('user', text);
    setResults({ entity: '', rows: [], expanded: {}, action: 'list' });

    try {
      const parsed = parseCommand(text);
      if (parsed.intent === 'smalltalk') {
        const replies = {
          greeting: [
            'Hi. I can help with customers, products, users, roles, sales, and reports.',
            'Hello. Tell me what you want to do and I will guide you step by step.',
            'Hey. You can type a command or tap a quick suggestion.'
          ],
          status: [
            'Doing well and ready to help. What do you want to work on?',
            'All set here. Want to list, add, update, or delete something?',
            'Ready when you are. Try "list customer" or "sales report today".'
          ],
          thanks: [
            'You are welcome.',
            'Happy to help.',
            'Anytime. What next?'
          ],
          help: [
            'I can help with customer, product, category, user, role, sales, reports, and license info. Choose a suggestion or type your command.',
            'Try commands like "list customer", "add product", "delete user <name>", "sales INV-1001", or "sales report weekly".',
            'You can ask for list/add/update/delete, sales details by invoice, or date-based reports.'
          ],
          ack: [
            'Great. What would you like to do next?',
            'Perfect. I am ready for the next task.',
            'Okay. Send the next command.'
          ],
          bye: [
            'Sure. I am here whenever you need help.',
            'Bye for now. Ping me anytime.',
            'See you. I will be ready when you come back.'
          ]
        };
        const kind = parsed.kind || 'help';
        setShowSuggestions(kind !== 'thanks' && kind !== 'ack' && kind !== 'bye');
        pushMessage('bot', pickOne(replies[kind] || replies.help));
      } else if (parsed.intent === 'unknown') {
        setShowSuggestions(true);
        pushMessage('bot', pickOne([
          'I did not fully get that. Choose a suggestion below or type a valid command.',
          'I can help, but I need a supported command. Pick a suggestion below.',
          'That looks outside my command set right now. Try a quick suggestion below.'
        ]));
      } else if (parsed.intent === 'view' && parsed.entity === 'license') {
        setShowSuggestions(false);
        const info = await fetchLicenseInfo();
        if (!info) {
          pushMessage('bot', 'No license info found.');
        } else {
          setResults({ entity: 'license', rows: [info], expanded: { license: true }, action: 'view' });
          pushMessage('bot', 'Showing current license info.');
        }
      } else if (parsed.intent === 'view' && parsed.entity === 'sale') {
        setShowSuggestions(false);
        const hit = await fetchSaleByInvoice(parsed.query || '');
        if (!hit.row) {
          if (hit.many?.length) {
            setResults({ entity: 'sale', rows: hit.many, expanded: {}, action: 'view' });
            pushMessage('bot', `Multiple sales matched "${parsed.query}". Select one with Details.`);
          } else {
            pushMessage('bot', `No sale found for invoice "${parsed.query}".`);
          }
        } else {
          const k = hit.row.id ?? hit.row.invoice_no ?? 'sale';
          setResults({ entity: 'sale', rows: [hit.row], expanded: { [k]: true }, action: 'view' });
          pushMessage('bot', `Showing details for invoice ${hit.row.invoice_no || parsed.query}.`);
        }
      } else if (parsed.intent === 'report') {
        setShowSuggestions(false);
        const range = parseReportRange(parsed.raw || '');
        const typ = parseReportType(parsed.raw || '');
        let reportRow;
        if (typ === 'returns') reportRow = await fetchReturnsReport(range);
        else if (typ === 'dashboard') reportRow = await fetchDashboardReport(range);
        else reportRow = await fetchSalesReport(range);
        setResults({ entity: 'report', rows: [reportRow], expanded: { [reportRow.id]: true }, action: 'view' });
        pushMessage('bot', `Showing ${typ} report for ${range.label}.`);
      } else if (parsed.intent === 'list') {
        setShowSuggestions(false);
        const rows = parsed.entity === 'customer'
          ? await fetchCustomers(parsed.query, 15)
          : parsed.entity === 'product'
          ? await fetchProducts(parsed.query, 15)
          : parsed.entity === 'category'
          ? await fetchCategories(parsed.query, 15)
          : parsed.entity === 'user'
          ? await fetchUsers(parsed.query, 15)
          : parsed.entity === 'sale'
          ? await fetchSalesList(parsed.query, 15)
          : await fetchRoles(parsed.query, 15);
        setResults({ entity: parsed.entity, rows, expanded: {}, action: 'list' });
        if (!rows.length) pushMessage('bot', `No ${parsed.entity}s found.`);
        else pushMessage('bot', `Showing ${rows.length} ${parsed.entity}(s). Expand and click Edit.`);
      } else {
        setShowSuggestions(false);
        await beginAction(parsed);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Action failed';
      pushMessage('bot', `Error: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    const text = input.trim();
    setInput('');
    await executeCommand(text);
  }

  function onDraftFieldChange(name, value) {
    if (draft?.entity === 'customer' && (name === 'state_code' || name === 'gstin')) {
      setDraftData((d) => ({ ...d, [name]: String(value || '').toUpperCase() }));
      return;
    }
    setDraftData((d) => ({ ...d, [name]: value }));
  }

  function renderDraftFields() {
    if (!draft || !draftData) return null;

    if (draft.entity === 'customer') {
      return (
        <div className="chatbot-form-grid">
          <label>Name</label><input value={draftData.name} onChange={(e) => onDraftFieldChange('name', e.target.value)} />
          <label>Phone</label><input value={draftData.phone} onChange={(e) => onDraftFieldChange('phone', e.target.value)} />
          <label>Email</label><input value={draftData.email} onChange={(e) => onDraftFieldChange('email', e.target.value)} />
          <label>Address</label><textarea value={draftData.address} onChange={(e) => onDraftFieldChange('address', e.target.value)} rows={2} />
          <label>State Code</label><input value={draftData.state_code} onChange={(e) => onDraftFieldChange('state_code', e.target.value)} disabled={draft.mode === 'update' && customerStateGuard.locked} />
          <label>GSTIN</label><input value={draftData.gstin} onChange={(e) => onDraftFieldChange('gstin', e.target.value)} />
          <label>Tags</label><input value={draftData.tags} onChange={(e) => onDraftFieldChange('tags', e.target.value)} placeholder="tag1, tag2" />
          <label>Store Credit</label><input value={draftData.store_credit} onChange={(e) => onDraftFieldChange('store_credit', e.target.value)} />
        </div>
      );
    }

    if (draft.entity === 'product') {
      return (
        <div className="chatbot-form-grid">
          <label>Name</label><input value={draftData.name} onChange={(e) => onDraftFieldChange('name', e.target.value)} />
          <label>SKU</label><input value={draftData.sku} onChange={(e) => onDraftFieldChange('sku', e.target.value)} />
          <label>Barcode</label><input value={draftData.barcode} onChange={(e) => onDraftFieldChange('barcode', e.target.value)} />
          <label>Price</label><input value={draftData.price} onChange={(e) => onDraftFieldChange('price', e.target.value)} />
          <label>GST Rate</label><input value={draftData.gst_rate} onChange={(e) => onDraftFieldChange('gst_rate', e.target.value)} />
          <label>Discount %</label><input value={draftData.discount_pct} onChange={(e) => onDraftFieldChange('discount_pct', e.target.value)} />
          <label>Stock</label><input value={draftData.stock} onChange={(e) => onDraftFieldChange('stock', e.target.value)} />
          <label>Low Stock Threshold</label><input value={draftData.low_stock_threshold} onChange={(e) => onDraftFieldChange('low_stock_threshold', e.target.value)} />
          <label>Category</label>
          <select value={String(draftData.category_id || '')} onChange={(e) => onDraftFieldChange('category_id', e.target.value)}>
            {categoryOptions.length === 0 && <option value="">Uncategorized</option>}
            {categoryOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name || `Category ${c.id}`}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (draft.entity === 'user') {
      return (
        <div className="chatbot-form-grid">
          <label>Name</label><input value={draftData.name} onChange={(e) => onDraftFieldChange('name', e.target.value)} />
          <label>Email</label><input value={draftData.email} onChange={(e) => onDraftFieldChange('email', e.target.value)} />
          {draft.mode === 'add' && (
            <>
              <label>Password</label><input type="password" value={draftData.password} onChange={(e) => onDraftFieldChange('password', e.target.value)} />
            </>
          )}
          <label>Active</label>
          <select value={draftData.is_active ? 'true' : 'false'} onChange={(e) => onDraftFieldChange('is_active', e.target.value === 'true')}>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <label>Role</label>
          <select value={String(draftData.role_name || '')} onChange={(e) => onDraftFieldChange('role_name', e.target.value)}>
            {roleOptions.length === 0 && <option value="">Select role</option>}
            {roleOptions.map((r) => (
              <option key={r.id || r.name} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (draft.entity === 'role') {
      return (
        <div className="chatbot-form-grid">
          <label>Name</label><input value={draftData.name} onChange={(e) => onDraftFieldChange('name', e.target.value)} disabled={draft.mode === 'update' && !!draftData.built_in} />
          <label>Description</label><input value={draftData.description} onChange={(e) => onDraftFieldChange('description', e.target.value)} disabled={draft.mode === 'update' && !!draftData.built_in} />
          <label>Permissions</label><input value={draftData.permissions} onChange={(e) => onDraftFieldChange('permissions', e.target.value)} placeholder="users:read, roles:update" />
        </div>
      );
    }

    return (
      <div className="chatbot-form-grid">
        <label>Name</label><input value={draftData.name} onChange={(e) => onDraftFieldChange('name', e.target.value)} />
      </div>
    );
  }

  function renderDraftBlock() {
    if (!draft || !draftData) return null;
    return (
      <div className="chatbot-draft">
        <div className="chatbot-draft-title">
          <b>{draft.mode.toUpperCase()} {draft.entity.toUpperCase()}</b>
          {draftData ? <span className="chatbot-muted">{markerLabel(draft.entity)}: {markerValue(draft.entity, draftData)}</span> : null}
        </div>
        {renderDraftFields()}
        {draft.entity === 'customer' && draft.mode === 'update' && customerStateGuard.locked && (
          <div className="chatbot-muted" style={{ marginTop: 6 }}>State code is locked after first sale.</div>
        )}
        {draft.entity === 'role' && draft.mode === 'update' && !!draftData?.built_in && (
          <div className="chatbot-muted" style={{ marginTop: 6 }}>Built-in role: name and description are locked. You can update permissions only.</div>
        )}
        {saveStatus && (
          <div className={`chatbot-save-status ${saveStatus.type}`}>{saveStatus.text}</div>
        )}
        <div className="chatbot-draft-actions">
          <button type="button" onClick={validateAndPush} disabled={busy}>Save</button>
          <button type="button" onClick={() => { setDraft(null); setDraftData(null); setSaveStatus(null); }} disabled={busy}>Cancel</button>
        </div>
      </div>
    );
  }

  function renderDetails(row, entity) {
    if (entity === 'customer') {
      return (
        <div className="chatbot-view-grid">
          <span>Name</span><strong>{row.name || '-'}</strong>
          <span>Phone</span><strong>{row.phone || '-'}</strong>
          <span>Email</span><strong>{row.email || '-'}</strong>
          <span>Address</span><strong>{row.address || '-'}</strong>
          <span>State</span><strong>{row.state_code || '-'}</strong>
          <span>GSTIN</span><strong>{row.gstin || '-'}</strong>
          <span>Tags</span><strong>{Array.isArray(row.tags) ? row.tags.join(', ') : (row.tags || '-')}</strong>
          <span>Store Credit</span><strong>{row.store_credit ?? 0}</strong>
        </div>
      );
    }
    if (entity === 'product') {
      return (
        <div className="chatbot-view-grid">
          <span>Name</span><strong>{row.name || '-'}</strong>
          <span>SKU</span><strong>{row.sku || '-'}</strong>
          <span>Barcode</span><strong>{row.barcode || '-'}</strong>
          <span>Price</span><strong>{row.price ?? 0}</strong>
          <span>GST Rate</span><strong>{row.gst_rate ?? 0}</strong>
          <span>Discount %</span><strong>{row.discount_pct ?? 0}</strong>
          <span>Stock</span><strong>{row.stock ?? 0}</strong>
          <span>Low Stock Threshold</span><strong>{row.low_stock_threshold ?? 0}</strong>
          <span>Category</span><strong>{row.category_name || row.category || (row.category_id ? `#${row.category_id}` : 'Uncategorized')}</strong>
        </div>
      );
    }
    if (entity === 'user') {
      return (
        <div className="chatbot-view-grid">
          <span>Name</span><strong>{row.name || '-'}</strong>
          <span>Email</span><strong>{row.email || '-'}</strong>
          <span>Status</span><strong>{row.is_active ? 'Active' : 'Inactive'}</strong>
          <span>Roles</span><strong>{Array.isArray(row.roles) ? row.roles.join(', ') : '-'}</strong>
        </div>
      );
    }
    if (entity === 'role') {
      return (
        <div className="chatbot-view-grid">
          <span>Name</span><strong>{row.name || '-'}</strong>
          <span>Description</span><strong>{row.description || '-'}</strong>
          <span>Built In</span><strong>{row.built_in ? 'Yes' : 'No'}</strong>
          <span>Users</span><strong>{row.users_count ?? 0}</strong>
          <span>Permissions</span><strong>{Array.isArray(row.permissions) ? row.permissions.join(', ') : '-'}</strong>
        </div>
      );
    }
    if (entity === 'license') {
      return (
        <div className="chatbot-view-grid">
          <span>Tenant</span><strong>{row.name || '-'}</strong>
          <span>Slug</span><strong>{row.slug || '-'}</strong>
          <span>Status</span><strong>{String(row.status || '-').toUpperCase()}</strong>
          <span>Valid From</span><strong>{row.license_valid_from ? new Date(row.license_valid_from).toLocaleDateString() : '-'}</strong>
          <span>Valid Till</span><strong>{row.license_valid_till ? new Date(row.license_valid_till).toLocaleDateString() : '-'}</strong>
          <span>Days Left</span><strong>{row.days_left ?? 0}</strong>
          <span>Read Only</span><strong>{row.requires_read_only ? 'Yes' : 'No'}</strong>
        </div>
      );
    }
    if (entity === 'report') {
      if (row.kind === 'sales') {
        return (
          <div className="chatbot-view-grid">
            <span>Range</span><strong>{row.from} to {row.to}</strong>
            <span>Bills</span><strong>{row.bills}</strong>
            <span>Total Sales</span><strong>{inr.format(row.total || 0)}</strong>
            <span>Average Bill</span><strong>{inr.format(row.avg_bill || 0)}</strong>
            <span>CGST</span><strong>{inr.format(row.cgst || 0)}</strong>
            <span>SGST</span><strong>{inr.format(row.sgst || 0)}</strong>
            <span>IGST</span><strong>{inr.format(row.igst || 0)}</strong>
            <span>Cash</span><strong>{inr.format(row.payment_cash || 0)}</strong>
            <span>UPI</span><strong>{inr.format(row.payment_upi || 0)}</strong>
            <span>Card/Others</span><strong>{inr.format(row.payment_card || 0)}</strong>
          </div>
        );
      }
      if (row.kind === 'returns') {
        return (
          <div className="chatbot-view-grid">
            <span>Range</span><strong>{row.from} to {row.to}</strong>
            <span>Returns Count</span><strong>{row.returns_count}</strong>
            <span>Total Returns</span><strong>{inr.format(row.total_returns || 0)}</strong>
            <span>Top Product</span><strong>{row.top_return_products?.[0]?.name || '-'}</strong>
            <span>Top Customer</span><strong>{row.top_return_customers?.[0]?.customer_name || '-'}</strong>
          </div>
        );
      }
      return (
        <div className="chatbot-view-grid">
          <span>Range</span><strong>{row.from} to {row.to}</strong>
          <span>Sales</span><strong>{inr.format(row.sales_total || 0)}</strong>
          <span>Returns</span><strong>{inr.format(row.returns_total || 0)}</strong>
          <span>Net</span><strong>{inr.format(row.net_after_returns || 0)}</strong>
          <span>Bills</span><strong>{row.sales_bills || 0}</strong>
          <span>Return Count</span><strong>{row.returns_count || 0}</strong>
        </div>
      );
    }
    if (entity === 'sale') {
      const items = Array.isArray(row.items) ? row.items : [];
      const itemSummary = items.slice(0, 5).map((it) => {
        const n = it?.name || it?.product_name || '-';
        const q = Number(it?.qty || it?.quantity || 0);
        return `${n} x ${q}`;
      }).join(', ');
      return (
        <div className="chatbot-view-grid">
          <span>Invoice</span><strong>{row.invoice_no || '-'}</strong>
          <span>Date</span><strong>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</strong>
          <span>Customer</span><strong>{row.customer_name || row.customer?.name || '-'}</strong>
          <span>Phone</span><strong>{row.customer_phone || row.customer?.phone || '-'}</strong>
          <span>Payment</span><strong>{row.payment_method || '-'}</strong>
          <span>Items</span><strong>{items.length}</strong>
          <span>Total</span><strong>{inr.format(Number(row.total || 0))}</strong>
          <span>Discount</span><strong>{inr.format(Number(row.discount || 0))}</strong>
          <span>Credit Applied</span><strong>{inr.format(Number(row.credit_applied || 0))}</strong>
          <span>GST</span><strong>{inr.format(Number(row.cgst || 0) + Number(row.sgst || 0) + Number(row.igst || 0))}</strong>
          <span>Line Items</span><strong>{itemSummary || '-'}</strong>
        </div>
      );
    }
    return (
      <div className="chatbot-view-grid">
        <span>Name</span><strong>{row.name || '-'}</strong>
      </div>
    );
  }

  function toggleExpanded(id) {
    setResults((r) => {
      const isCurrentlyExpanded = !!r.expanded[id];
      const nextExpanded = { ...r.expanded, [id]: !isCurrentlyExpanded };
      if (isCurrentlyExpanded && draft && Number(draft.id) === Number(id)) {
        setDraft(null);
        setDraftData(null);
        setSaveStatus(null);
      }
      return { ...r, expanded: nextExpanded };
    });
  }

  function editRow(row) {
    if (results.entity === 'license' || results.entity === 'report' || results.entity === 'sale') return;
    const entity = results.entity;
    const mode = results.action === 'delete' ? 'delete' : 'update';
    setSelected((s) => ({ ...s, [entity]: row }));
    setResults((r) => ({ ...r, expanded: { ...r.expanded, [row.id]: true } }));
    openDraft(entity, mode, row, row.id);
  }

  const boxStyle = { left: position.x, top: position.y };
  const visibleSuggestions = showAllSuggestions
    ? quickSuggestions
    : quickSuggestions.slice(0, SUGGESTION_PREVIEW_COUNT);
  const canExpandSuggestions = quickSuggestions.length > SUGGESTION_PREVIEW_COUNT;

  return (
    <div className={`chatbot-root ${open ? 'open' : 'closed'} theme-${theme}`} style={boxStyle}>
      {open ? (
        <div className="chatbot-panel">
          <div className="chatbot-head" onMouseDown={onDragStart}>
            <div className="chatbot-head-title">
              <strong>Support Chat</strong>
              <small>Sellantra assistant</small>
            </div>
            <div className="chatbot-head-actions">
              <button type="button" className="chatbot-theme-btn" aria-label="Switch theme" onClick={cycleTheme}>🎨</button>
              <button type="button" onClick={() => toggleOpen(false)} aria-label="Minimize">_</button>
            </div>
          </div>

          <div className="chatbot-body" ref={scrollRef}>
            {messages.map((m, i) => <Message key={i} role={m.role} text={m.text} />)}

            {results.rows.length > 0 && (
              <div className="chatbot-results">
                {results.rows.map((row, idx) => {
                  const rowKey = row.id ?? `row-${idx}`;
                  const expanded = !!results.expanded[rowKey];
                  const inlineDraft =
                    draft &&
                    draft.mode !== 'add' &&
                    draft.entity === results.entity &&
                    Number(draft.id) === Number(row.id);
                  const isSelected =
                    selected?.[results.entity] &&
                    Number(selected[results.entity].id) === Number(row.id);
                  return (
                    <div key={rowKey} className={`chatbot-card ${isSelected ? 'selected' : ''}`}>
                      <div className="chatbot-card-row">
                        <div>
                          <b>{results.entity === 'sale' ? (row.invoice_no || '-') : (row.name || '-')}</b>
                          <div className="chatbot-muted">{markerLabel(results.entity)}: {markerValue(results.entity, row)}</div>
                          {results.entity === 'sale' && (
                            <div className="chatbot-muted">
                              Customer: {row.customer_name || '-'} | Phone: {row.customer_phone || '-'}
                            </div>
                          )}
                        </div>
                        <div className="chatbot-card-actions">
                          <button type="button" onClick={() => toggleExpanded(rowKey)}>{expanded ? 'Collapse' : 'Details'}</button>
                          {results.entity !== 'license' && results.entity !== 'report' && results.entity !== 'sale' && (
                            <button type="button" onClick={() => editRow(row)}>{results.action === 'delete' ? 'Delete' : 'Edit'}</button>
                          )}
                        </div>
                      </div>
                      {expanded && (
                        <div className="chatbot-view">{renderDetails(row, results.entity)}</div>
                      )}
                      {expanded && inlineDraft && renderDraftBlock()}
                    </div>
                  );
                })}
              </div>
            )}

            {draft && draft.mode === 'add' && renderDraftBlock()}

            {showSuggestions && !draft && (
              <div className="chatbot-suggestions">
                {visibleSuggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={busy}
                    onClick={() => executeCommand(s)}
                  >
                    {s}
                  </button>
                ))}
                {canExpandSuggestions && (
                  <button
                    type="button"
                    className="chatbot-suggestion-more"
                    disabled={busy}
                    onClick={() => setShowAllSuggestions((v) => !v)}
                  >
                    {showAllSuggestions ? 'Less' : 'More'}
                  </button>
                )}
              </div>
            )}

          </div>

          <form className="chatbot-input" onSubmit={onSubmit}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a command..."
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()}>{busy ? '...' : 'Send'}</button>
          </form>
        </div>
      ) : (
        <button
          className="chatbot-fab"
          type="button"
          onMouseDown={onDragStart}
          onClick={() => { if (!dragMoved) toggleOpen(true); }}
        >
          Chat
        </button>
      )}
    </div>
  );
}


